import { Dialog, Grow } from "@mui/material";
import Button from "components/common/Button";
import { setSelected, toggleGlobalSearchModal } from "store/slices/main.slice";
import CloseIcon from "@mui/icons-material/Close";
import Input from "components/common/Input";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import Loading from "components/common/Loading";
import ProductPopUp from "./ProductPopUp";
import api from "config/api";
import Product from "./Product";
import { ORDER } from "utils/constants";
import { isProductAvailableForOrderType } from "utils/orderTypeAvailability";

const SearchDialogTransition = forwardRef(function SearchDialogTransition(
  props: any,
  ref: any,
) {
  return (
    <Grow
      ref={ref}
      {...props}
      timeout={{ enter: 320, exit: 180 }}
      style={{
        transformOrigin: "top right",
        ...props.style,
      }}
    />
  );
});

const GlobalSearchProduct = () => {
  const dispatch = useAppDispatch();
  const showGlobalSearchModal = useAppSelector(
    ({ main }) => main.showGlobalSearchModal,
  );
  const { settings, deliveryType, sideBarMenu } = useAppSelector(({ main }) => main);
  const activeOrderType =
    deliveryType === "pickup" ? ORDER.TAKE_OUT : ORDER.DELIVERY;

  const [products, setproducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState<any>("");

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Infinite scroll (client-side progressive render) + scroll container ref
  const LIST_STEP = 12;
  const [visibleCount, setVisibleCount] = useState(LIST_STEP);
  const listRef = useRef<HTMLDivElement | null>(null);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Theme-based translucent styles (no hard-coded UI colors)
  const ui = useMemo(() => {
    const themeHex = (settings as any)?.ThemeColor || "#111827";
    const themeSel = (settings as any)?.ThemeSelectedColor || "#ffffff";

    const hexToRgba = (hex: string, alpha = 1) => {
      const clean = String(hex || "").replace("#", "").trim();
      const full =
        clean.length === 3
          ? clean
              .split("")
              .map((c) => c + c)
              .join("")
          : clean.padEnd(6, "0").slice(0, 6);
      const r = parseInt(full.slice(0, 2), 16) || 0;
      const g = parseInt(full.slice(2, 4), 16) || 0;
      const b = parseInt(full.slice(4, 6), 16) || 0;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    return {
      themeHex,
      themeSel,
      border: hexToRgba(themeHex, 0.18),
      softBg: hexToRgba(themeHex, 0.06),
      hoverBg: hexToRgba(themeHex, 0.12),
      headerBg: hexToRgba(themeHex, 0.06),
      headerBg2: hexToRgba(themeHex, 0.02),
      ring: hexToRgba(themeHex, 0.28),
      shadow: "0 22px 60px rgba(15,23,42,0.18)",
    };
  }, [settings]);
  // === END SAMPATH ===

  const normalizeProductsForView = (items: any[] = []) =>
    window.location.pathname.includes("admin")
      ? items
      : items.filter((item: any) =>
          isProductAvailableForOrderType(item, activeOrderType)
        );

  const mergeUniqueProducts = (items: any[] = []) => {
    const map = new Map();
    items.forEach((item: any) => {
      const key = Number(item?.productId || item?.id || item?.ProductId || 0);
      if (key && !map.has(key)) {
        map.set(key, item);
      }
    });
    return Array.from(map.values());
  };

  const searchMatchesProduct = (item: any, query: string) => {
    if (!query) return true;

    const lowerQuery = query.toLowerCase();
    return [
      item?.productName,
      item?.productCode,
      item?.productDescription,
      item?.pricedesc,
    ]
      .filter((value) => value !== null && value !== undefined)
      .some((value) => String(value).toLowerCase().includes(lowerQuery));
  };

  const fetchAvailableCategoryProducts = () =>
    Promise.all(
      (sideBarMenu || []).map(({ id }) =>
        api
          .get(`/api/Product/ByCategoryId?categoryId=${id}`)
          .then(({ data }) =>
            (Array.isArray(data) ? data : []).map((item: any) => ({
              ...item,
              prodcat: item?.prodcat ?? item?.prodCat ?? item?.categoryId ?? id,
              __categoryId: item?.__categoryId ?? id,
            }))
          )
          .catch(() => []),
      ),
    ).then((results) => mergeUniqueProducts(results.flat()));

  const fetchData = () => {
    if (!showGlobalSearchModal) return;

    setLoading(true);
    setproducts([]);

    const trimmedSearch = String(search || "").trim();
    const isAdminPage = window.location.pathname.includes("admin");
    const request =
      trimmedSearch && isAdminPage
        ? api.get(`/api/Product/ByName?name=${encodeURIComponent(trimmedSearch)}`)
        : fetchAvailableCategoryProducts().then((items) => ({
            data: trimmedSearch
              ? items.filter((item) => searchMatchesProduct(item, trimmedSearch))
              : items,
          }));

    request
      .then(({ data }) => {
        const nextProducts = normalizeProductsForView(Array.isArray(data) ? data : []);
        setproducts(nextProducts);
        // === ADDED BY SAMPATH – 2026-01-15 ===
        // Reset progressive render when new results arrive
        setVisibleCount(LIST_STEP);
        // === END SAMPATH ===
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData();
    }, 200);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeOrderType, showGlobalSearchModal, sideBarMenu]);

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Client-side infinite scroll: load more items when near bottom
  const onScrollList = () => {
    const el = listRef.current;
    if (!el) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 120;
    if (nearBottom && !loading) {
      setVisibleCount((prev) => Math.min(prev + LIST_STEP, products.length));
    }
  };

  const visibleProducts = useMemo(
    () => products.slice(0, visibleCount),
    [products, visibleCount],
  );
  // === END SAMPATH ===

  return (
    <Dialog
      onClose={() => {
        dispatch(toggleGlobalSearchModal());
        setSearch("");
        setproducts([]);
        // === ADDED BY SAMPATH – 2026-01-15 ===
        setVisibleCount(LIST_STEP);
        // === END SAMPATH ===
      }}
      open={showGlobalSearchModal}
      TransitionComponent={SearchDialogTransition}
      maxWidth="md"
      fullWidth
      className="search-modal relative"
      // === ADDED BY SAMPATH – 2026-01-15 ===
      PaperProps={{
        sx: {
          borderRadius: "28px",
          overflow: "hidden",
          border: `1px solid ${ui.border}`,
          boxShadow: ui.shadow,
          background: "#ffffff",
        },
      }}
      // === END SAMPATH ===
    >
      <style>
        {`
          .search-modal .MuiDialog-paper,
          .search-modal .MuiDialog-paper * {
            -webkit-font-smoothing: antialiased;
            text-rendering: geometricPrecision;
            letter-spacing: 0;
          }
          .dcs-search-results > div > div {
            border-color: rgba(16, 24, 40, .10) !important;
            border-radius: 16px !important;
            box-shadow: none !important;
          }
          .dcs-search-results h3 {
            color: #111827 !important;
            font-size: 17px !important;
            line-height: 1.25 !important;
            font-weight: 650 !important;
          }
          .dcs-search-results p,
          .dcs-search-results span {
            color: #1f2937;
          }
          .dcs-search-results p {
            line-height: 1.45 !important;
            font-weight: 400 !important;
          }
        `}
      </style>
      {/* === ADDED BY SAMPATH – 2026-01-15 === */}
      {/* NEW header: modern + compact + search inside header (old look removed) */}
      <div
        className="sticky top-0 z-20"
        style={{
          background: "#ffffff",
          borderBottom: `1px solid ${ui.border}`,
        }}
      >
        <div className="px-4 sm:px-5 pt-5 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[28px] sm:text-[31px] font-[650] text-[#111827] leading-tight">
                Search
              </div>
              <div className="text-[13px] sm:text-[14px] font-normal text-[#475467] mt-1.5">
                Find products fast and add to cart
              </div>
            </div>

            <Button
              className="!min-w-0 !w-11 !h-11 !px-0 !py-0 !rounded-full shrink-0"
              style={{
                background: "rgba(255,255,255,0.86)",
                border: `1px solid ${ui.border}`,
                boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
              }}
              onClick={() => {
                dispatch(toggleGlobalSearchModal());
                setSearch("");
                setproducts([]);
                setVisibleCount(LIST_STEP);
              }}
            >
              <CloseIcon style={{ color: "#111827" }} />
            </Button>
          </div>

          <div className="mt-3">
            <div
              className="rounded-2xl p-3 sm:p-4"
              style={{
                background: "#ffffff",
                border: `1px solid ${ui.border}`,
                boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
              }}
            >
              <Input
                size="small"
                label="Search Product"
                className="w-full"
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    backgroundColor: "#fff",
                    fontSize: "15px",
                    color: "#111827",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "13px",
                    color: "#475467",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "15px",
                    fontWeight: 400,
                    color: "#111827",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: ui.ring,
                    },
                }}
                onValueChange={(e) => {
                  setSearch(e);
                }}
                value={search}
                type="text"
              />

              <div className="mt-2 flex items-center justify-between text-[12px] font-normal text-[#475467]">
                <div>
                  {loading ? (
                    "Loading products..."
                  ) : products.length > 0 ? (
                    <>
                      Showing{" "}
                      <span className="font-semibold text-[#111827]">
                        {Math.min(visibleCount, products.length)}
                      </span>{" "}
                      of{" "}
                      <span className="font-semibold text-[#111827]">{products.length}</span>
                    </>
                  ) : (
                    "Products load when search opens"
                  )}
                </div>
                <div className="hidden sm:block">
                  Scroll to load more results
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* === END SAMPATH === */}

      {/* === ADDED BY SAMPATH – 2026-01-15 === */}
      {/* Scroll area fixed height so modal scroll works correctly */}
      <div
        ref={listRef}
        onScroll={onScrollList}
        className="px-4 sm:px-5 py-4 overflow-y-auto"
        style={{
          // professional: consistent modal body height + smooth scrolling
          maxHeight: "70vh",
        }}
      >
        {/* Empty state */}
        {!loading && showGlobalSearchModal && products.length === 0 && (
          <div
            className="rounded-[24px] p-6 text-center"
            style={{
              background: "rgba(255,255,255,0.82)",
              border: `1px dashed ${ui.border}`,
            }}
          >
            <div className="text-base font-semibold">No products found</div>
            <div className="text-sm opacity-70 mt-1">
              {search ? "Try a different keyword." : "No products available right now."}
            </div>
          </div>
        )}

        <div className="dcs-search-results grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
          {visibleProducts.map((item) => (
            <div
              key={item?.id || item?.productId || item?.ProductId || item?.name}
              className="rounded-2xl p-3 transition-all duration-200"
              style={{
                background: "#ffffff",
                border: `1px solid ${ui.border}`,
                boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background =
                  "#ffffff";
                (e.currentTarget as HTMLDivElement).style.boxShadow =
                  "0 14px 30px rgba(15,23,42,0.10)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background =
                  "#ffffff";
                (e.currentTarget as HTMLDivElement).style.boxShadow =
                  "0 10px 24px rgba(15,23,42,0.06)";
              }}
            >
              <Product
                {...item}
                btnText={
                  window.location.pathname.includes("admin")
                    ? "Edit"
                    : "Add to cart"
                }
                onClick={() => {
                  dispatch(setSelected(item));
                }}
              />
            </div>
          ))}
        </div>

        {/* Loading (API fetch) */}
        {loading && (
          <div className="h-44 flex items-center justify-center">
            <Loading />
          </div>
        )}

        {/* === ADDED BY SAMPATH – 2026-01-15 === */}
        {/* Progressive load indicator (when more items exist) */}
        {!loading && products.length > 0 && visibleCount < products.length && (
          <div
            className="mt-4 rounded-[20px] px-4 py-3 text-center text-sm"
            style={{
              background: "rgba(255,255,255,0.82)",
              border: `1px solid ${ui.border}`,
            }}
          >
            Scroll down to load more…
          </div>
        )}
        {/* === END SAMPATH === */}

        <ProductPopUp />
      </div>
      {/* === END SAMPATH === */}
    </Dialog>
  );
};

export default GlobalSearchProduct;
